import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";
import { Bar, Pie } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const FraudCharts = () => {
  const [stateData,    setStateData]    = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [ownersByState, setOwnersByState] = useState([]);
  const [ownersByDistrict, setOwnersByDistrict] = useState([]);
  const [monthData,    setMonthData]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedOwnerState, setSelectedOwnerState] = useState("");

  const fetchData = async () => {
    try {
      const headers = adminAuthHeader();
      const results = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/admin/analytics/state`,    { headers }),
        axios.get(`${API_BASE_URL}/api/admin/analytics/district`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/analytics/owners-by-state`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/analytics/owners-by-district`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/analytics/month`,    { headers }),
      ]);

      const responseData = result => result.status === "fulfilled" ? result.value.data : null;
      const fraudStates = responseData(results[0]);
      const fraudDistricts = responseData(results[1]);
      let ownerStates = responseData(results[2]);
      let ownerDistricts = responseData(results[3]);
      const fraudMonths = responseData(results[4]);

      setStateData(Array.isArray(fraudStates) ? fraudStates : []);
      setDistrictData(Array.isArray(fraudDistricts) ? fraudDistricts : []);
      setMonthData(Array.isArray(fraudMonths) ? fraudMonths : []);

      const hasStateRows = Array.isArray(ownerStates) && ownerStates.every(
        item => typeof item?._id === "string" && typeof item.count === "number"
      );
      const hasDistrictRows = Array.isArray(ownerDistricts) && ownerDistricts.every(
        item => typeof item?._id?.state === "string" && typeof item?._id?.district === "string"
          && typeof item.count === "number"
      );

      if (!hasStateRows || !hasDistrictRows) {
        const ownersResponse = await axios.get(`${API_BASE_URL}/api/admin/owners?limit=1000`, { headers });
        const profiles = ownersResponse.data?.owners || [];
        const stateCounts = new Map();
        const districtCounts = new Map();

        profiles.forEach(profile => {
          const state = profile.state?.trim() || "Unknown";
          const district = profile.district?.trim() || "Unknown";
          stateCounts.set(state, (stateCounts.get(state) || 0) + 1);
          const key = JSON.stringify([state, district]);
          districtCounts.set(key, (districtCounts.get(key) || 0) + 1);
        });

        if (!hasStateRows) {
          ownerStates = [...stateCounts].map(([_id, count]) => ({ _id, count }));
        }
        if (!hasDistrictRows) {
          ownerDistricts = [...districtCounts].map(([key, count]) => {
            const [state, district] = JSON.parse(key);
            return { _id: { state, district }, count };
          });
        }
      }

      setOwnersByState(Array.isArray(ownerStates) ? ownerStates : []);
      setOwnersByDistrict(Array.isArray(ownerDistricts) ? ownerDistricts : []);
    } catch (err) {
      console.error("Failed to load charts:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const COLORS = ["#4f46e5","#ef4444","#22c55e","#f59e0b","#06b6d4","#8b5cf6","#14b8a6","#f97316"];

  const stateChart = {
    labels: stateData.map(i => i._id || "Unknown"),
    datasets: [{ label: "Frauds by State", data: stateData.map(i => i.count), backgroundColor: COLORS }]
  };

  const districtChart = {
    labels: districtData.map(i => i._id || "Unknown"),
    datasets: [{ data: districtData.map(i => i.count), backgroundColor: COLORS }]
  };

  const visibleOwnerDistricts = ownersByDistrict.filter(
    item => !selectedOwnerState || item._id.state === selectedOwnerState
  );
  const ownersByDistrictChart = {
    labels: visibleOwnerDistricts.map(item => selectedOwnerState
      ? item._id.district
      : `${item._id.district} (${item._id.state})`),
    datasets: [{
      label: "Registered owners",
      data: visibleOwnerDistricts.map(item => item.count),
      backgroundColor: "#168c83",
      borderRadius: 4,
    }],
  };
  const ownersByStateChart = {
    labels: ownersByState.map(item => item._id || "Unknown state"),
    datasets: [{
      label: "Registered owners",
      data: ownersByState.map(item => item.count),
      backgroundColor: "#0891b2",
      borderRadius: 4,
    }],
  };
  const ownersByDistrictOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const monthChart = {
    labels: monthData.map(i => MONTH_NAMES[(i._id - 1)] || `Month ${i._id}`),
    datasets: [{ label: "Frauds per Month", data: monthData.map(i => i.count), backgroundColor: "#4f46e5" }]
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#a3aed0" }}>Loading charts…</div>
  );

  // Show message if no data yet
  if (stateData.length === 0 && monthData.length === 0 && ownersByState.length === 0) return (
    <div style={{ textAlign: "center", padding: "40px", color: "#a3aed0" }}>
      <div style={{ fontSize: "36px", marginBottom: "8px" }}>📊</div>
      <p style={{ fontWeight: "600", color: "#1b2559" }}>No chart data yet</p>
      <p style={{ fontSize: "13px" }}>Charts will appear once fraud reports are approved</p>
    </div>
  );

  return (
    <div className="admin-charts">
      <div className="chart-card">
        <h3>Registered Users by State</h3>
        {ownersByState.length ? (
          <div className="chart-box" style={{ height: Math.max(240, ownersByState.length * 36) }}>
            <Bar data={ownersByStateChart} options={ownersByDistrictOptions} />
          </div>
        ) : <p style={{ color: "#a3aed0", fontSize: 13 }}>No registered user state data yet</p>}
      </div>
      <div className="chart-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Registered Users by District</h3>
          <select
            aria-label="Filter user districts by state"
            value={selectedOwnerState}
            onChange={event => setSelectedOwnerState(event.target.value)}
            style={{ minWidth: 180, height: 36, padding: "0 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#374151" }}
          >
            <option value="">All states</option>
            {ownersByState.map(item => <option key={item._id} value={item._id}>{item._id}</option>)}
          </select>
        </div>
        {visibleOwnerDistricts.length ? (
          <div className="chart-box" style={{ height: Math.max(300, visibleOwnerDistricts.length * 28) }}>
            <Bar data={ownersByDistrictChart} options={ownersByDistrictOptions} />
          </div>
        ) : <p style={{ color: "#a3aed0", fontSize: 13 }}>No registered user district data for this state yet</p>}
      </div>
      <div className="chart-card">
        <h3>Frauds by State</h3>
        <div className="chart-box"><Bar data={stateChart} /></div>
      </div>
      <div className="chart-card">
        <h3>Frauds by District / City</h3>
        <div className="chart-box"><Pie data={districtChart} /></div>
      </div>
      <div className="chart-card">
        <h3>Frauds by Month</h3>
        <div className="chart-box"><Bar data={monthChart} /></div>
      </div>
    </div>
  );
};

export default FraudCharts;
